/**
 * mobile-more-sheet.tsx — Hộp "Thêm" của bản điện thoại: trượt từ dưới lên ~2/3 màn hình, hai phần:
 *  ① AI hỗ trợ — chọn AI (Trợ lý chung / nhân viên AI của Đội AI) rồi chọn việc: báo cáo hôm nay / 7 ngày, việc cần xử lý… (chỉ
 *     hiện tác vụ vai trò có quyền — máy chủ lấy số liệu theo phạm vi của người dùng) hoặc hỏi đáp tự do → mở khung AI Trợ lý
 *     (hộp trượt cao gần hết màn) chạy luôn tác vụ.
 *  ② Ứng dụng — 4 ô (khác các tab dưới, ưu tiên dùng được trên điện thoại; ô chỉ dùng trên máy tính gắn nhãn "PC") + "Xem thêm" →
 *     màn "Tất cả ứng dụng" dạng thẻ vuông, nhóm theo menu (lib/mobile-apps.ts).
 * Hồ sơ / gói dịch vụ / cài đặt / đăng xuất: ngăn "Cá nhân" (mobile-account-drawer.tsx) xổ ngang từ trái — bấm ảnh đại diện ở đầu màn Nhắn tin.
 */
import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  BarChart3,
  ListTodo,
  MessageCircleQuestion,
  Monitor,
  Sparkles,
  TrendingUp,
  X,
  type LucideIcon,
} from 'lucide-react'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { navForRole } from './nav-config'
import { useAuthStore } from '@/stores/auth-store'
import { useMyPermissions } from '@/hooks/use-settings'
import { useAiBots } from '@/hooks/use-ai'
import { useAssistantTasks, type AssistantTask } from '@/hooks/use-ai-assistant'
import { buildMobileApps, featuredApps, groupApps, type MobileApp } from '@/lib/mobile-apps'
import { splitMobileNav } from '@/lib/mobile-nav'
import { mobileNav } from '@/lib/mobile-scope'
import { AiAssistantPanel } from '@/pages/conversations/ai-assistant-panel'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

/** Ô "Hỏi đáp tự do" — không phải tác vụ máy chủ, chỉ mở khung chat. */
const QA_KEY = '__qa__'

/** `hintKey` = khoá trong `shell:more.taskHints`. */
const TASK_META: Record<string, { icon: LucideIcon; hintKey: string }> = {
  report_today: { icon: BarChart3, hintKey: 'reportToday' },
  report_week: { icon: TrendingUp, hintKey: 'reportWeek' },
  pending_work: { icon: ListTodo, hintKey: 'pendingWork' },
  [QA_KEY]: { icon: MessageCircleQuestion, hintKey: 'qa' },
}

export function MobileMoreSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { pathname } = useLocation()
  const { t, i18n } = useTranslation('shell')
  const user = useAuthStore((s) => s.user)
  const role = user?.role
  const { data: perms } = useMyPermissions()
  const botsQ = useAiBots()
  const tasksQ = useAssistantTasks(open)

  const [botId, setBotId] = useState<string | undefined>(undefined)
  const [assistant, setAssistant] = useState<{ key: number; task: AssistantTask | null } | null>(null)
  const [allApps, setAllApps] = useState(false)

  // Nhãn ứng dụng dịch lúc dựng → dựng lại khi đổi ngôn ngữ.
  const apps = useMemo(() => buildMobileApps(navForRole(role), perms), [role, perms, i18n.language])
  const tabPaths = useMemo(() => splitMobileNav(mobileNav(navForRole(role))).tabs.map((t) => t.to), [role])
  const featured = useMemo(() => featuredApps(apps, tabPaths), [apps, tabPaths])
  const bots = (botsQ.data?.bots ?? []).filter((b) => b.enabled)
  const tasks = tasksQ.data ?? []

  if (!user) return null
  const closeAll = () => {
    setAllApps(false)
    onOpenChange(false)
  }
  const runTask = (task: AssistantTask | null) => setAssistant({ key: Date.now(), task })

  return (
    <>
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <BottomSheetContent title={t('more.title')} className="h-[66dvh]">
          <div className="space-y-5 px-4 pb-5 pt-2">
            <p className="text-base font-semibold">{t('more.utilities')}</p>

            {/* ① AI hỗ trợ */}
            <section className="space-y-3 rounded-2xl border border-orange-100 bg-gradient-to-b from-orange-50 to-background p-3 dark:border-orange-900/40 dark:from-orange-950/30">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Sparkles className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-tight">{t('more.aiHelp')}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{t('more.aiHelpHint')}</p>
                </div>
              </div>
              <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 pb-0.5" role="radiogroup" aria-label={t('more.pickAi')}>
                <AgentChip emoji="🤖" label={t('more.generalAssistant')} active={!botId} onClick={() => setBotId(undefined)} />
                {bots.map((b) => (
                  <AgentChip key={b.id} emoji={b.avatarEmoji || '🤖'} label={b.name} active={botId === b.id} onClick={() => setBotId(b.id)} />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {tasks.map((task) => (
                  <TaskTile key={task.key} taskKey={task.key} label={task.label} onClick={() => runTask(task)} />
                ))}
                <TaskTile taskKey={QA_KEY} label={t('more.freeQa')} onClick={() => runTask(null)} />
              </div>
            </section>

            {/* ② Ứng dụng */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">{t('more.apps')}</p>
                {apps.length > featured.length && (
                  <button type="button" onClick={() => setAllApps(true)} className="text-xs font-medium text-primary active:opacity-70">
                    {t('more.seeAll', { count: apps.length })}
                  </button>
                )}
              </div>
              {featured.length > 0 ? (
                <ul className="grid grid-cols-4 gap-x-2 gap-y-3">
                  {featured.map((a) => (
                    <li key={a.key}>
                      <AppCard app={a} active={isAt(pathname, a.to)} onClick={closeAll} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">{t('more.noApps')}</p>
              )}
            </section>
          </div>
        </BottomSheetContent>
      </BottomSheet>

      {/* Màn "Tất cả ứng dụng" — thẻ vuông, nhóm theo menu */}
      <BottomSheet open={allApps} onOpenChange={setAllApps}>
        <BottomSheetContent title={t('more.allApps')} className="h-[100dvh] max-h-[100dvh] rounded-none pt-[env(safe-area-inset-top)]">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-4 py-2.5">
            <p className="text-base font-semibold">{t('more.allApps')}</p>
            <button type="button" onClick={() => setAllApps(false)} aria-label={t('common:actions.close')} className="rounded-full p-1.5 active:bg-muted">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-5 px-4 pb-6 pt-4">
            {groupApps(apps).map((g) => (
              <section key={g.group} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.group}</p>
                <ul className="grid grid-cols-4 gap-x-2 gap-y-3">
                  {g.apps.map((a) => (
                    <li key={a.key}>
                      <AppCard app={a} active={isAt(pathname, a.to)} onClick={closeAll} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </BottomSheetContent>
      </BottomSheet>

      {/* Khung AI Trợ lý — chạy luôn tác vụ đã chọn với AI đã chọn */}
      <BottomSheet open={!!assistant} onOpenChange={(v) => !v && setAssistant(null)}>
        <BottomSheetContent title={t('more.aiAssistant')} className="h-[92dvh] max-h-[92dvh]">
          {assistant && (
            <div className="h-full">
              <AiAssistantPanel
                key={assistant.key}
                active
                initialBotId={botId}
                initialTask={assistant.task}
                onClose={() => setAssistant(null)}
              />
            </div>
          )}
        </BottomSheetContent>
      </BottomSheet>

    </>
  )
}

const isAt = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`)

function AgentChip({ emoji, label, active, onClick }: { emoji: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
        active ? 'border-primary bg-primary text-primary-foreground' : 'border-orange-200 bg-background text-foreground dark:border-orange-900/50',
      )}
    >
      <span className="text-sm leading-none">{emoji}</span>
      <span className="max-w-[8rem] truncate">{label}</span>
    </button>
  )
}

function TaskTile({ taskKey, label, onClick }: { taskKey: string; label: string; onClick: () => void }) {
  const { t } = useTranslation('shell')
  const meta = TASK_META[taskKey]
  const hint = meta ? t(`more.taskHints.${meta.hintKey}`) : ''
  const Icon = meta?.icon ?? Sparkles
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-2 rounded-xl border bg-background p-2.5 text-left active:bg-muted">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-xs font-semibold">{label}</span>
        {hint && <span className="block truncate text-[10.5px] text-muted-foreground">{hint}</span>}
      </span>
    </button>
  )
}

/** Ô ứng dụng dạng thẻ vuông; ứng dụng chỉ dùng trên máy tính có nhãn "PC" (bấm vẫn mở màn nhắc + gửi liên kết sang máy tính). */
function AppCard({ app, active, onClick }: { app: MobileApp; active: boolean; onClick: () => void }) {
  const { t } = useTranslation('shell')
  const Icon = app.icon
  return (
    <Link
      to={app.to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={app.desktopOnly ? t('more.desktopOnly', { label: app.label }) : app.label}
      className="flex flex-col items-center gap-1.5 text-center active:opacity-70"
    >
      <span
        className={cn(
          'relative flex aspect-square w-full max-w-[64px] items-center justify-center rounded-2xl border',
          active ? 'border-primary bg-primary text-primary-foreground' : app.desktopOnly ? 'bg-muted/60 text-muted-foreground' : 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="h-6 w-6" />
        {app.desktopOnly && (
          <span className="absolute -right-1 -top-1 inline-flex items-center gap-0.5 rounded-full border bg-background px-1 py-px text-[9px] font-semibold leading-none text-muted-foreground shadow-sm">
            <Monitor className="h-2.5 w-2.5" /> PC
          </span>
        )}
      </span>
      <span className={cn('line-clamp-2 text-[11px] leading-tight', active ? 'font-medium text-primary' : 'text-foreground')}>{app.label}</span>
    </Link>
  )
}

